import React, { useEffect, useState } from "react";
import { batch, connect } from "react-redux";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import { handleHTTPError } from "../../_actions/errorHandlerActions";

import {
  changeTemplate,
  setSelectedText,
  updateDocument,
  toggleInputForm,
  prependAiResults,
  updatePrompt,
  toggleResultGeneration,
  fetchAiTemplates
} from "../../_actions/aiDocumentActions";
import { DOCUMENTS_ENDPOINT, DOCUMENT_DETAILS } from "../../_actions/endpoints";
import { checkCeleryTaskStatus } from "../../_utils/common";
import Format from "string-format";
import { AI_TEMPLATE } from "../../_utils/routes";
import DomainNameForm from "./DomainNameForm";
import WorkspaceForm from "./WorkspaceForm";
import EditorWorkspaceForm from "./EditorWorkspaceForm";
import { showToast } from "../../_utils/showToast";
import { useTranslation } from "react-i18next";
import { ShowCenterSpinner } from "../../_components/common/statelessView";
import { isEmpty } from "lodash";
import { Box, Spinner } from "@chakra-ui/react";
import TldrUpgradeSubscriptionModal from "../../_components/common/modals/TldrUpgradeSubscriptionModal";
import { aiIncludedFeatures } from "../../_utils/constants";
import aiLongFormArtBoard from "../../assets/images/aiLongFormArtBoard.jpeg";
import { StyledSpinner } from "../../_components/styled/ai/styledAi";

export const AiInputForm = (props) => {
  const { t } = useTranslation(["ai", "common"]);
  const { prompt, document, resultsView } = props;
  const { data } = document;
  const signalToken = axios.CancelToken.source();
  const history = useHistory();
  const { slug } = useParams();

  const [writerPage, setWriterPage] = useState(1);
  const [currentPrompt, setCurrentPrompt] = useState(
    prompt ? prompt : data.prompt
  );
  const [showWorkspaceForm, setShowWorkpaceForm] = useState(
    !prompt?.content?.domain || false
  );
  const [showUpgradeSubscriptionModal, setShowUpgradeSubscriptionModal] =
    useState(false);

  const [lastRun, setLastRun] = useState({
    title: data?.payload ? data.payload.title : "",
    keywords: data?.payload ? data.payload.keywords : "",
    description: data?.payload ? data.payload.description : "",
    action: data?.prompt ? data.prompt.template : "copy",
    output: data?.payload?.output ?? "en",
    tone: data?.payload?.tone ?? "Formal",
    id: data?.id,
  });

  function showFailedMessage(error, setSubmitting) {
    if (error) {
      props.handleHTTPError(error, props);
    } else {
      showToast({
        description: t("sorry_msg_desc"),
        title: t("sorry_msg"),
        status: "error",
      });
    }

    if (setSubmitting) {
      setSubmitting(false);
    }
  }

  function createNewDocument(payload) {
    batch(() => {
      props.toggleInputForm(true);
      props.toggleResultGeneration(true);
    });

    axios
      .post(DOCUMENTS_ENDPOINT, payload, { cancelToken: signalToken.token })
      .then((res) => {
        checkCeleryTaskStatus(res.data.task_id, () => {}).then((result) => {
          props.toggleResultGeneration(false);
          history.push(Format(AI_TEMPLATE, res.data.id));
          if (Array.isArray(result.response.info)) {
            props.prependAiResults(result.response.info);
          } else if (result.response.info?.error) {
            showToast({
              description: result.response.info?.error,
              title: t("try_again"),
              status: "error",
            });
          }
        });
      })
      .catch((error) => {
        setShowUpgradeSubscriptionModal(true);
        props.toggleResultGeneration(false);
        showFailedMessage(error);
        handleHTTPError(error, props);
      });
  }

  function updateDocumentRequest(payload) {
    batch(() => {
      props.toggleResultGeneration(true);
    });
    axios
      .patch(Format(DOCUMENT_DETAILS, data.id), payload, {
        cancelToken: signalToken.token,
      })
      .then((res) => {
        props.updateDocument(res.data);
        checkCeleryTaskStatus(res.data.task_id, () => {})
          .then((result) => {
            props.toggleResultGeneration(false);
            batch(() => {
              if (Array.isArray(result.response.info)) {
                props.toggleInputForm(true);
                props.prependAiResults(result.response.info);
              } else if (result.response.info?.error) {
                showToast({
                  description: result.response.info?.error,
                  title: t("try_again"),
                  status: "error",
                });
              }
            });
          })
          .catch((error) => {
            showFailedMessage(error);
            props.toggleResultGeneration(false);
          });
      })
      .catch((error) => {
        setShowUpgradeSubscriptionModal(true);
        showFailedMessage(error);
        props.toggleResultGeneration(false);
      });
  }

  useEffect(() => {
    if (slug) {
      fetchPrompt(1);
    } else if (data.prompt) {
      setCurrentPrompt(data.prompt);
    }
    return () => {
      signalToken.cancel("The user aborted a request.");
    };
  }, [data]);

  function onTemplateChange(selectedTemplate) {
    props.changeTemplate(props, selectedTemplate, signalToken).then((res) => {
      fetchPrompt(1, res.prompt);
    });
  }

  function fetchPrompt(page = 1, prompt = {}) {
    if (!isEmpty(prompt)) {
      setCurrentPrompt(prompt);
      return;
    }

    if (props?.aiTemplates?.length) {
      const prompt = props.aiTemplates.find(
        (template) => template.slug === slug
      );
      setCurrentPrompt(prompt);
    } else {
      props
        .fetchAiTemplates(props, "", signalToken, page, "")
        .then((templates) => {
          const prompt = templates.find((template) => template.slug === slug);
          if (!prompt) {
            setWriterPage(writerPage + 1);
            fetchPrompt(writerPage + 1);
          }
          setCurrentPrompt(prompt);
        });
    }
  }

  return (
    <>
      {!showWorkspaceForm && (
        <DomainNameForm
          props={props}
          onError={() => {
            setShowWorkpaceForm(true);
          }}
          onComplete={(data) => {
            if (data) {
              let companyInfo = {
                ...lastRun,
                title: data.title ? data.title : "",
                keywords: data.keywods ? data.keywords : "",
                description: data.description ? data.description : "",
              };
              setLastRun(companyInfo);
            }
            setShowWorkpaceForm(true);
          }}
        ></DomainNameForm>
      )}

      {!currentPrompt && <Spinner as={StyledSpinner} size="xl" top="40%" left="47%"/>}

      {!resultsView && currentPrompt ? (
        <>
        <WorkspaceForm
          onTemplateChange={onTemplateChange}
          setShowWorkpaceForm={setShowWorkpaceForm}
          lastRun={lastRun}
          props={props}
          onCompleteForm={props.onComplete}
          onComplete={(promptId, values) => {
            if (data?.id) {
              updateDocumentRequest({ payload: values, prompt: promptId });
            } else {
              createNewDocument({
                title: currentPrompt.title + " document",
                prompt: currentPrompt.id,
                payload: values,
              });
            }
          }}
          prompt={currentPrompt ? currentPrompt : data.prompt}
          backbtn={props.backbtn}
          showbreadCrumb={true}
          isMobileView={props.isMobileView}
        
        ></WorkspaceForm>
        </>
      ) : resultsView && currentPrompt ? (
        <EditorWorkspaceForm
          onTemplateChange={onTemplateChange}
          resultsView={props.resultsView}
          setShowWorkpaceForm={setShowWorkpaceForm}
          lastRun={lastRun}
          props={props}
          onComplete={(promptId, values) => {
            if (data?.id) {
              updateDocumentRequest({ payload: values, prompt: promptId });
            } else {
              createNewDocument({
                title: currentPrompt.title + " document",
                prompt: currentPrompt.id,
                payload: values,
              });
            }
          }}
          prompt={currentPrompt ? currentPrompt : data.prompt}
          handleCollapsibleSectionClick={() => {
            props.toggleInputForm(!props.document.isInputFormCollapsed);
          }}
        ></EditorWorkspaceForm>
      ) : null}
      {showUpgradeSubscriptionModal ? (
        <TldrUpgradeSubscriptionModal
          featureList={aiIncludedFeatures}
          show={showUpgradeSubscriptionModal}
          onHide={() => setShowUpgradeSubscriptionModal(false)}
          title={t("ai_feature_heading", { ns: "ai" })}
          image={aiLongFormArtBoard}
        />
      ) : null}
    </>
  );
};

AiInputForm.propTypes = {};

const mapStateToProps = (state) => ({
  document: state.document,
  subscription: state.subscription,
  aiTemplates: state.document.aiTemplates,
  isMobileView: state.app.isMobileView
});

const mapDispatchToProps = (dispatch) => {
  return {
    handleHTTPError: (error, props) => dispatch(handleHTTPError(error, props)),
    prependAiResults: (payload) => dispatch(prependAiResults(payload)),
    updateDocument: (payload) => dispatch(updateDocument(payload)),
    changeTemplate: (props, selectedTemplate, signalToken) =>
      dispatch(changeTemplate(props, selectedTemplate, signalToken)),
    setSelectedText: (selectedText) => dispatch(setSelectedText(selectedText)),
    toggleInputForm: (state) => dispatch(toggleInputForm(state)),
    toggleResultGeneration: (state) => dispatch(toggleResultGeneration(state)),
    updatePrompt: (prompt) => dispatch(updatePrompt(prompt)),
    fetchAiTemplates: (props, key, signalToken, page, searchTerm) =>
      dispatch(fetchAiTemplates(props, key, signalToken, page, searchTerm)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AiInputForm);
